import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import type { GeneralServices, ServiceForm, ServicesHero } from '../../api/api-types';
import type { Loadable, Loaded } from '../../guards/ngrx-loader.guard';
import { actions } from './services.actions';

interface ServicesState {
    servicesHero: Loadable<ServicesHero>;
    generalServices: Loadable<GeneralServices>;
    serviceFormContent: Loadable<ServiceForm>;
    submitFormSuccess: boolean | null;
    submitFormLoading: boolean;
}

const initialState: ServicesState = {
    servicesHero: null,
    generalServices: null,
    serviceFormContent: null,
    submitFormSuccess: null,
    submitFormLoading: false,
};

export const state = createFeature({
    name: 'services',
    reducer: createReducer(
        initialState,
        on(
            actions.loadServicesHeroSuccess,
            (state, { servicesHero }): ServicesState => ({
                ...state,
                servicesHero,
            }),
        ),
        on(
            actions.loadServicesHeroFailure,
            (state): ServicesState => ({
                ...state,
                servicesHero: false,
            }),
        ),
        on(actions.loadGeneralServicesSuccess, (state, { generalServices }): ServicesState => {
            return {
                ...state,
                generalServices,
            };
        }),
        on(actions.loadGeneralServicesFailure, (state): ServicesState => {
            return {
                ...state,
                generalServices: false,
            };
        }),
        on(actions.loadServiceForm, (state): ServicesState => {
            return {
                ...state,
                submitFormSuccess: null,
            };
        }),
        on(actions.loadServiceFormSuccess, (state, { serviceFormContent }): ServicesState => {
            return {
                ...state,
                serviceFormContent,
            };
        }),
        on(actions.loadServiceFormFailure, (state): ServicesState => {
            return {
                ...state,
                serviceFormContent: false,
            };
        }),
        on(actions.submitServiceFormResponse, (state, action): ServicesState => {
            return {
                ...state,
                submitFormSuccess: action.success,
                submitFormLoading: false,
            };
        }),
        on(actions.submitServiceFormLoading, (state, action): ServicesState => {
            return {
                ...state,
                submitFormLoading: action.loading,
            };
        }),
    ),
    extraSelectors: (baseSelectors) => {
        const selectIsLoadingHeroServices = createSelector(
            baseSelectors.selectServicesHero,
            (heroServices) => heroServices === null,
        );
        const selectLoadedServicesHero = createSelector(
            baseSelectors.selectServicesHero,
            (heroServices) => heroServices as Loaded<ServicesHero>,
        );
        const selectIsLoadingServiceForm = createSelector(
            baseSelectors.selectServiceFormContent,
            (selectServiceFormContent) => selectServiceFormContent === null,
        );
        const selectLoadedServiceForm = createSelector(
            baseSelectors.selectServiceFormContent,
            (selectServiceFormContent) => selectServiceFormContent as Loaded<ServiceForm>,
        );
        return {
            selectIsLoadingHeroServices,
            selectLoadedServicesHero,
            selectIsLoadingServiceForm,
            selectLoadedServiceForm,
        };
    },
});
